import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './DiamondSection.css';
import '../common-styles.css';
import roughVideo from '../../assets/videos/rough.mp4';
import cutVideo from '../../assets/videos/cut.mp4';
import polishVideo from '../../assets/videos/polish.mp4';

interface TabContent {
  title: string;
  description: string;
  buttonText: string;
}

type TabKey = 'rough' | 'cut' | 'polish';

const DiamondSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('polish');
  const navigate = useNavigate();

  const tabContent: Record<TabKey, TabContent> = {
    rough: {
      title: "Raw Diamond Formation",
      description: "Before a diamond dazzles the world, it begins as a rough stone—raw, untouched, and filled with potential. Sourced responsibly, each rough is carefully examined using advanced scanning systems to map its internal features. This critical step determines how best to shape the diamond’s natural form, ensuring beauty, symmetry, and brilliance from the very beginning of its transformation.",
      buttonText: "Rough"
    },
    cut: {
      title: "Precision Diamond Cutting", 
      description: "Once the rough is mapped, the diamond moves to the cutting stage, where skilled artisans and advanced technology work in harmony. Each facet is shaped with precision to achieve perfect symmetry, brilliance, and fire. This phase defines how light will travel through the stone—unlocking its inner beauty and preparing it for the final polish that reveals its timeless and radiant character.",
      buttonText: "Cut"
    },
    polish: {
      title: "A Symbol of Timeless Brilliance.",
      description: "After the diamond is fully polished, it is submitted to leading gemological laboratories—such as GIA or IGI—for grading and certification. Here, its cut, color, clarity, and carat weight are formally documented. Once certified, the diamond is presented in its final form, ready to be set in fine jewelry or cherished as a standalone gem—radiating elegance, authenticity, and timeless beauty.",
      buttonText: "Polish"
    }
  };
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // map tab key to video source
  const videoMap: Record<TabKey, string> = {
    rough: roughVideo,
    cut: cutVideo,
    polish: polishVideo,
  };

  useEffect(() => {
    // when activeTab changes, attempt to load and play the video
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.src = videoMap[activeTab];
      // reload and play; ignore play promise
      v.load();
      const p = v.play();
      if (p && typeof p.then === 'function') p.catch(() => {});
    }
  }, [activeTab]);
  return (
    <section className="section diamond-section">
      <Container>
        {/* Header Badge */}
        <Row className="section-header mb-5">
          <Col>
            <div>
              <div className="diamond-badge">DIAMOND</div>
            </div>
          </Col>
        </Row>

        <Row className="diamond-content">
          {/* Left Side - Buttons and Image */}
          <Col lg={6} className="diamond-left">
            <div className="diamond-tabs">
              {(Object.keys(tabContent) as TabKey[]).map((tab) => (
                <Button
                  key={tab}
                  className={`section-btn diamond-tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  <span className="btn-content">
                    {tabContent[tab].buttonText}
                  </span>
                </Button>
              ))}
            </div>
            <div className="diamond-image-container">
              <div className="diamond-image-wrapper">
                <div className="diamond-image">
                  <video
                    ref={videoRef}
                    className="diamond-video"
                    src={videoMap[activeTab]}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label="Diamond process video"
                  />
                </div>
              </div>
            </div>
          </Col>

          {/* Right Side - Content */}
          <Col lg={6} className="diamond-right">
            <div className="diamond-content-wrapper">
              <h2 className="section-title">
                <span className="section-title-highlight">A Symbol of</span> {tabContent[activeTab].title.replace('A Symbol of ', '')}
              </h2>
              <p className="diamond-description">{tabContent[activeTab].description}</p>
              <Button className="section-btn" onClick={()=> navigate('/diamonds')}>
                <span className="btn-content">
                  Learn More <i className="fas fa-arrow-right btn-arrow-diagonal" />
                </span>
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default DiamondSection;
